import { Vector2 } from "three";
import type * as THREE from "three";
import type { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";

/**
 * Render the current scene at the supplied pixel dimensions, encode as PNG,
 * and trigger a download. Renders through the EffectComposer so the bloom
 * pass lands in the export. Restores the original size afterward so the
 * on-screen view isn't disturbed.
 */
export async function exportDesignPng(
  composer: EffectComposer,
  renderer: THREE.WebGLRenderer,
  filename: string,
  width = 1920,
  height = 1080,
): Promise<void> {
  const prevSize = renderer.getSize(new Vector2());
  const prevPixelRatio = renderer.getPixelRatio();

  renderer.setPixelRatio(1);
  renderer.setSize(width, height, false);
  composer.setSize(width, height);

  composer.render();

  const blob: Blob | null = await new Promise((resolve) =>
    renderer.domElement.toBlob(resolve, "image/png"),
  );

  renderer.setPixelRatio(prevPixelRatio);
  renderer.setSize(prevSize.x, prevSize.y, false);
  composer.setSize(prevSize.x, prevSize.y);

  if (!blob) throw new Error("toBlob returned null");

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitize(filename) || "neon-sign"}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function sanitize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
